import { IconAlertCircle } from "@tabler/icons-react";
import { Alert, Badge, Divider, Group, Stack, Text } from "@/components/ui";
import { formatearMonto } from "@/shared/format/money";
import type { CFDIDeSolicitud } from "@/lib/types";

const VIGENCIA_LABEL: Record<string, string> = {
  Vigente: "Vigente ante el SAT",
  Cancelado: "Cancelado ante el SAT",
  NoEncontrado: "No encontrado en el SAT",
};

/** Contenido del accordion "CFDI cargado" · todos los campos clave + status SAT. */
export function CfdiContent({ cfdi }: { cfdi: CFDIDeSolicitud }) {
  const campos: Array<{ label: string; value: string; mono?: boolean; bold?: boolean }> = [
    { label: "UUID", value: cfdi.uuid, mono: true },
    { label: "Versión", value: cfdi.version },
    { label: "Tipo", value: cfdi.tipoComprobante },
    { label: "Moneda", value: cfdi.moneda },
    { label: "Fecha emisión", value: new Date(cfdi.fechaEmision).toLocaleDateString("es-MX") },
    {
      label: "Subtotal",
      value: formatearMonto(cfdi.subtotal, cfdi.moneda, { minimumFractionDigits: 2 }),
    },
    {
      label: "Total",
      value: formatearMonto(cfdi.total, cfdi.moneda, { minimumFractionDigits: 2 }),
      bold: true,
    },
  ];

  return (
    <Stack gap="md">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {campos.map((c) => (
          <Stack key={c.label} gap={2}>
            <Text size="xs" c="dimmed" tt="uppercase" className="tracking-wide" style={{ fontSize: 10 }}>
              {c.label}
            </Text>
            <Text size="sm" ff={c.mono ? "mono" : undefined} fw={c.bold ? 600 : 400}>
              {c.value}
            </Text>
          </Stack>
        ))}
      </div>
      <Divider />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <PartyCell label="Emisor" rfc={cfdi.rfcEmisor} nombre={cfdi.nombreEmisor} />
        <PartyCell label="Receptor" rfc={cfdi.rfcReceptor} nombre={cfdi.nombreReceptor} />
      </div>
      <SatDetalle cfdi={cfdi} />
      {cfdi.statusFiscalMensajeAmigable && (
        <Alert variant="warning" icon={<IconAlertCircle size={16} />} title="Aviso del SAT">
          <Stack gap={4}>
            <Text size="sm">{cfdi.statusFiscalMensajeAmigable}</Text>
            {cfdi.statusFiscalMensajeOriginal && (
              <Text size="xs" c="dimmed">
                Mensaje original: {cfdi.statusFiscalMensajeOriginal}
              </Text>
            )}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

/** Detalle enriquecido de la consulta al SAT · vigencia · cancelación · EFOS. */
function SatDetalle({ cfdi }: { cfdi: CFDIDeSolicitud }) {
  const estado = cfdi.satEstadoComprobante;
  const efos = cfdi.satValidacionEfos;
  const cancelacion = cfdi.satEstatusCancelacion;

  const tieneVigencia = !!estado && estado !== "Desconocido";
  const tieneEfos = efos === "Listado" || efos === "NoListado";
  const tieneCancelacion = !!cancelacion;

  if (!tieneVigencia && !tieneEfos && !tieneCancelacion) {
    return null;
  }

  return (
    <>
      <Divider label="Consulta SAT" />
      <Group gap="xs">
        {tieneVigencia && (
          <Badge color={estado === "Vigente" ? "exito" : "peligro"}>
            {VIGENCIA_LABEL[estado!] ?? estado}
          </Badge>
        )}
        {tieneEfos && (
          <Badge color={efos === "Listado" ? "peligro" : "exito"}>
            {efos === "Listado" ? "En lista EFOS (69-B)" : "Sin coincidencia EFOS"}
          </Badge>
        )}
        {tieneCancelacion && <Badge color="advertencia">Cancelación: {cancelacion}</Badge>}
      </Group>
      {cfdi.satConsultadoAt && (
        <Text size="xs" c="dimmed">
          Consultado el {new Date(cfdi.satConsultadoAt).toLocaleString("es-MX")}
          {cfdi.satProvider === "Real" ? " · SAT en línea" : ""}
        </Text>
      )}
    </>
  );
}

function PartyCell({ label, rfc, nombre }: { label: string; rfc: string; nombre: string }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" className="tracking-wide" style={{ fontSize: 10 }}>
        {label}
      </Text>
      <code className="w-fit rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{rfc}</code>
      <Text size="sm">{nombre}</Text>
    </Stack>
  );
}
